import Pica from 'pica';

const TARGET_W = 1920;
const TARGET_H = 1080;
const JPEG_QUALITY = 0.9;
const SAFARI_MAX_AREA = 14_000_000;

let picaInstance = null;
function getPica() {
  if (!picaInstance) picaInstance = new Pica();
  return picaInstance;
}

export async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch (_) {
      // fall through to <img> fallback
    }
  }
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = err => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

function getSize(bitmap) {
  return {
    width: bitmap.naturalWidth || bitmap.width,
    height: bitmap.naturalHeight || bitmap.height,
  };
}

export async function maybePreResize(bitmap) {
  const { width, height } = getSize(bitmap);
  const area = width * height;
  if (area <= SAFARI_MAX_AREA) return bitmap;
  const scale = Math.sqrt(SAFARI_MAX_AREA / area);
  const newW = Math.max(1, Math.round(width * scale));
  const newH = Math.max(1, Math.round(height * scale));
  const src = document.createElement('canvas');
  src.width = width;
  src.height = height;
  src.getContext('2d').drawImage(bitmap, 0, 0);
  const dst = document.createElement('canvas');
  dst.width = newW;
  dst.height = newH;
  await getPica().resize(src, dst, { alpha: false });
  return dst;
}

export async function buildPreviewDataUrl(source, crop) {
  if (!crop || !crop.width || !crop.height) return null;
  const { width, height } = getSize(source);
  const sx = Math.max(0, Math.min(crop.x, width));
  const sy = Math.max(0, Math.min(crop.y, height));
  const sw = Math.max(1, Math.min(crop.width, width - sx));
  const sh = Math.max(1, Math.min(crop.height, height - sy));
  const cropped = document.createElement('canvas');
  cropped.width = sw;
  cropped.height = sh;
  const ctx = cropped.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, sw, sh);
  ctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);
  const preview = document.createElement('canvas');
  preview.width = 256;
  preview.height = 144;
  await getPica().resize(cropped, preview, { alpha: false, quality: 2 });
  return preview.toDataURL('image/jpeg', 0.7);
}

export async function exportCrop(source, crop) {
  const { width, height } = getSize(source);
  const sx = Math.max(0, Math.min(crop.x, width));
  const sy = Math.max(0, Math.min(crop.y, height));
  const sw = Math.max(1, Math.min(crop.width, width - sx));
  const sh = Math.max(1, Math.min(crop.height, height - sy));

  const cropped = document.createElement('canvas');
  cropped.width = sw;
  cropped.height = sh;
  const cctx = cropped.getContext('2d');
  cctx.fillStyle = '#ffffff';
  cctx.fillRect(0, 0, sw, sh);
  cctx.drawImage(source, sx, sy, sw, sh, 0, 0, sw, sh);

  const target = document.createElement('canvas');
  target.width = TARGET_W;
  target.height = TARGET_H;
  await getPica().resize(cropped, target, {
    alpha: false,
    quality: 3,
    unsharpAmount: 80,
    unsharpRadius: 0.6,
    unsharpThreshold: 2,
  });
  const blob = await getPica().toBlob(target, 'image/jpeg', JPEG_QUALITY);
  return { blob, width: TARGET_W, height: TARGET_H };
}

export function buildOutputFilename(originalName) {
  const base = (originalName || 'image').replace(/\.[^.]+$/, '');
  return `${base}-${TARGET_W}x${TARGET_H}.jpg`;
}

export { TARGET_W, TARGET_H };
